// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
import type {
  DocumentResult,
  JobDefinition,
  LearningAccelerationReport,
  LearningAreaEligibility,
  LearningEligibility,
  LearningFieldMeasurement,
  LearningTableMeasurement,
} from './desktop-api';
import { layoutsOfType } from './layout-type-names';

/** An engine-reported count, or null when the value is not one (absent, negative, fractional). */
export const learningCount = (value: unknown): number | null =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;

/**
 * One thing a job can learn, and how far its operator has taught it. Shared: Learning shows these, and main counts
 * them for the business snapshot (trial telemetry `skills_*`).
 *
 * Every state is a projection of an engine-owned count. Nothing here sets a
 * threshold: "N more" is the engine's own remaining requirement, "Ready" is
 * the engine's own readiness or a proven map, and "In use" marks a Ready skill
 * with recorded use on current documents. When the evidence for a skill was not
 * reported, the state is `null` and the screen says so instead of guessing.
 */
export type SkillState = 'todo' | 'learning' | 'learned' | 'use';
export type SkillKind = 'type' | 'field' | 'table' | 'starts';
export type LearningSkill = {
  key: string;
  kind: SkillKind;
  label: string;
  /** Document type name; null for the job-wide document-starts skill. */
  typeName: string | null;
  state: SkillState | null;
  /** Engine-reported examples and remaining requirement, when the skill is learning. */
  have?: number;
  need?: number;
  /** More confirmed examples still needed, only when the engine's own readiness requirement is
   * that count (the type's minimum, or an area's one open "documents" requirement). */
  examplesNeeded?: number;
  /** One plain sentence that says where the state came from. */
  detail: string;
  /** Set with a null state while the engine is still counting its page designs (`layouts.summaryPending`). */
  counting?: true;
};
export type LearningSkillGroup = {
  key: string;
  name: string;
  kind: 'type' | 'starts';
  skills: LearningSkill[];
};
/** `taught` has at least one example; `ready` is learned or in use. */
export type SkillCounts = Record<SkillState, number> & {
  unknown: number;
  total: number;
  taught: number;
  ready: number;
};

/** Three states an operator sees: Not taught, Learning, Ready. "In use" is a Ready skill with recorded use. */
export const SKILL_STATE_LABEL: Record<SkillState, string> = {
  todo: 'Not taught',
  learning: 'Learning',
  learned: 'Ready',
  use: 'In use',
};
export const SKILL_STATE_HINT: Record<SkillState, string> = {
  todo: 'No confirmed examples yet',
  learning: 'Needs more confirmed examples',
  learned: 'Has enough confirmed examples',
  use: 'Ready, and used on current documents',
};

const plural = (count: number, one: string, many = `${one}s`) =>
  `${count.toLocaleString()} ${count === 1 ? one : many}`;
const LEARNED_SOURCE = /^(layout map|learned scalar route)\b/i;

function requirementProgress(area: LearningAreaEligibility | undefined) {
  if (!area?.requirements.length) return null;
  const counts = area.requirements.map((requirement) => ({
    current: learningCount(requirement.current),
    remaining: learningCount(requirement.remaining),
  }));
  if (counts.some((count) => count.current === null || count.remaining === null)) return null;
  return {
    current: Math.max(...counts.map((count) => count.current!)),
    remaining: Math.max(...counts.map((count) => count.remaining!)),
  };
}

function fromArea(
  area: LearningAreaEligibility | undefined,
  base: Omit<LearningSkill, 'state' | 'detail'>,
): LearningSkill | null {
  const progress = requirementProgress(area);
  if (!area || !progress) return null;
  if (area.ready)
    return {
      ...base,
      state: 'learned',
      detail: 'Has enough confirmed examples. Training can use them.',
    };
  const open = area.requirements.filter((requirement) => !requirement.ready);
  const examples =
    !area.holdReason && open.length === 1 && open[0].id === 'documents'
      ? learningCount(open[0].remaining)
      : null;
  if (examples) base = { ...base, examplesNeeded: examples };
  if (progress.current === 0)
    return {
      ...base,
      state: 'todo',
      detail: area.requirements[0]?.label
        ? `No confirmed examples yet: ${area.requirements[0].label.toLowerCase()}.`
        : SKILL_STATE_HINT.todo,
    };
  if (area.holdReason)
    return { ...base, state: 'learning', have: progress.current, detail: area.holdReason };
  return {
    ...base,
    state: 'learning',
    have: progress.current,
    need: progress.remaining,
    detail: area.requirements
      .filter((requirement) => !requirement.ready)
      .map((requirement) => `${requirement.remaining} more: ${requirement.label.toLowerCase()}`)
      .join(' · '),
  };
}

/** Skills grouped by document type, then the job-wide document-starts skill. */
export function projectLearningSkills({
  job,
  eligibility,
  report,
  fieldResults = [],
  tableResults = [],
  documents = null,
}: {
  job: JobDefinition;
  eligibility: LearningEligibility | null;
  report: LearningAccelerationReport | null;
  fieldResults?: readonly LearningFieldMeasurement[];
  tableResults?: readonly LearningTableMeasurement[];
  /** Latest batch only. Per-field use is not reported job-wide, so current documents are the only honest source. */
  documents?: readonly DocumentResult[] | null;
}): LearningSkillGroup[] {
  const areas =
    eligibility?.jobId === job.id ? eligibility.areas.filter((area) => area.applicable) : null;
  // While the engine is still counting its page designs, `layouts` is a placeholder: no layout in it
  // proves anything, and its absence proves nothing either. A state only page designs could settle
  // is then unknown, unless other engine evidence proves it.
  const counting = report?.layouts?.summaryPending === true;
  const stillCounting = {
    state: null,
    counting: true as const,
    detail: 'Still counting what this job has learned.',
  };
  const groups: LearningSkillGroup[] = job.documentTypes.map((type) => {
    const typeLayouts = counting ? [] : layoutsOfType(type, report);
    const skills: LearningSkill[] = [];

    // Recognize the type.
    const evidence = report?.documentTypes?.find((entry) => entry.documentType === type.name);
    const observed = learningCount(
      report?.layouts?.observedUse?.version === 1
        ? (report.layouts.observedUse.typeDocuments[type.name] ?? 0)
        : null,
    );
    const fromExamples = learningCount(evidence?.recognisedFromConfirmedExamples);
    const recognised =
      (observed ?? 0) + (fromExamples ?? 0) ||
      typeLayouts.reduce(
        (sum, layout) => sum + (learningCount(layout.recognisedFromLayout) ?? 0),
        0,
      );
    const confirmed = learningCount(evidence?.confirmed),
      minimum = learningCount(evidence?.minimumToTest);
    const typeBase = {
      key: `${type.id}:type`,
      kind: 'type' as const,
      label: 'Recognize',
      typeName: type.name,
    };
    if (!report) skills.push({ ...typeBase, state: null, detail: 'Not reported yet.' });
    else if (recognised > 0)
      skills.push({
        ...typeBase,
        state: 'use',
        detail: `Recognized ${plural(recognised, 'current document')} on its own.`,
      });
    else if (typeLayouts.some((layout) => layout.canAnswerType))
      skills.push({
        ...typeBase,
        state: 'learned',
        detail: 'Learned to recognize it from confirmed examples.',
      });
    else if (confirmed && minimum && confirmed >= minimum)
      skills.push({
        ...typeBase,
        state: 'learned',
        detail: `Learned from ${plural(confirmed, 'confirmed example')}.`,
      });
    // A page design the engine has not finished counting may already answer this type.
    else if (counting) skills.push({ ...typeBase, ...stillCounting });
    else if (confirmed && minimum && minimum > confirmed)
      skills.push({
        ...typeBase,
        state: 'learning',
        have: confirmed,
        need: minimum - confirmed,
        examplesNeeded: minimum - confirmed,
        detail: `${plural(minimum - confirmed, 'more confirmed example')} before it can be tested.`,
      });
    else if (confirmed)
      skills.push({
        ...typeBase,
        state: 'learning',
        have: confirmed,
        detail: `Learning from ${plural(confirmed, 'confirmed example')}.`,
      });
    else
      skills.push({
        ...typeBase,
        state: 'todo',
        ...(confirmed === 0 && minimum ? { examplesNeeded: minimum } : {}),
        detail: 'No confirmed examples of this type yet.',
      });

    // Fields: engine readiness per type and field; use only from current documents.
    for (const field of type.fields) {
      const kind = field.kind.trim().toLowerCase();
      if (kind === 'document-type' || kind === 'line-items') continue;
      const scope = `${type.name} · ${field.name}`;
      const base = {
        key: `${type.id}:${field.id}`,
        kind: 'field' as const,
        label: field.name,
        typeName: type.name,
      };
      const used =
        documents?.filter(
          (document) =>
            document.documentType === type.name &&
            document.fields.some(
              (value) =>
                (value.fieldId ? value.fieldId === field.id : value.name === field.name) &&
                LEARNED_SOURCE.test(value.source ?? ''),
            ),
        ).length ?? 0;
      if (used > 0) {
        skills.push({
          ...base,
          state: 'use',
          detail: `Read on its own in ${plural(used, 'current document')}.`,
        });
        continue;
      }
      const adopted = fieldResults.some(
        (result) =>
          result.documentType === type.name &&
          result.field === field.name &&
          result.applied &&
          result.outcome === 'validated',
      );
      if (adopted) {
        skills.push({
          ...base,
          state: 'learned',
          detail: 'Training kept a tested way to read it.',
        });
        continue;
      }
      const area = areas?.find(
        (entry) =>
          (entry.id === 'fields' || entry.id === 'barcode-reading') && entry.scope === scope,
      );
      skills.push(
        fromArea(area, base) ?? {
          ...base,
          state: areas ? 'todo' : null,
          detail: areas ? 'No confirmed examples yet.' : 'Not reported yet.',
        },
      );
    }

    // Line items.
    if (type.fields.some((field) => field.kind.trim().toLowerCase() === 'line-items')) {
      const base = {
        key: `${type.id}:table`,
        kind: 'table' as const,
        label: 'Line items',
        typeName: type.name,
      };
      const usedDocuments = typeLayouts.reduce(
        (sum, layout) =>
          sum +
          (learningCount(layout.fullTableMapUsedDocuments) ?? 0) +
          (learningCount(layout.numericTableMapUsedDocuments) ?? 0),
        0,
      );
      const mapped = typeLayouts.some(
        (layout) =>
          (learningCount(layout.tableMapBuiltFrom) ?? 0) > 0 ||
          (learningCount(layout.numericTableMapBuiltFrom) ?? 0) > 0,
      );
      if (usedDocuments > 0)
        skills.push({
          ...base,
          state: 'use',
          detail: `Read line items in ${plural(usedDocuments, 'current document')} on its own.`,
        });
      else if (
        mapped ||
        tableResults.some((result) => result.documentType === type.name && result.ready)
      )
        skills.push({ ...base, state: 'learned', detail: 'A learned table map is ready.' });
      // Table maps are counted with the page designs, so none reported yet is not none learned.
      else if (counting) skills.push({ ...base, ...stillCounting });
      else {
        const area = areas?.find((entry) => entry.id === 'tables' && entry.scope === type.name);
        skills.push(
          fromArea(area, base) ?? {
            ...base,
            state: areas ? 'todo' : null,
            detail: areas ? 'No confirmed examples yet.' : 'Not reported yet.',
          },
        );
      }
    }
    return { key: type.id, name: type.name, kind: 'type' as const, skills };
  });

  // Where documents start: job-wide.
  const boundaries = areas?.find((area) => area.id === 'boundaries');
  if (boundaries) {
    const base = {
      key: 'starts',
      kind: 'starts' as const,
      label: 'Where documents start',
      typeName: null,
    };
    const use =
      report?.improvementUse?.version === 5 && (report.improvementUse.boundaryImprovements ?? 0) > 0
        ? learningCount(report.improvementUse.boundaryPages)
        : null;
    const skill = use
      ? {
          ...base,
          state: 'use' as const,
          detail: `Placed ${plural(use, 'page')} using what it learned.`,
        }
      : fromArea(boundaries, base);
    if (skill)
      groups.push({
        key: 'starts',
        name: 'Document starts',
        kind: 'starts',
        skills: [{ ...skill, need: undefined }],
      });
  }
  return groups;
}

/**
 * A document type's place in Learning's journey for its Document types card: the part furthest behind
 * (owner, 2026-10-19), so the card says what Learning says and the two never disagree. One part not
 * taught is Not taught; any part still learning is Learning; Ready only when every part is Ready or in
 * use. A part not reported yet (or still being counted) could be behind, so nothing is claimed past it.
 */
export type TypeLearningState = 'todo' | 'learning' | 'ready';
export function typeLearningState(group: LearningSkillGroup | undefined): TypeLearningState | null {
  if (!group?.skills.length) return null;
  const states = group.skills.map((skill) => skill.state);
  if (states.includes('todo')) return 'todo';
  if (states.includes(null)) return null;
  return states.includes('learning') ? 'learning' : 'ready';
}

export function countSkills(groups: readonly LearningSkillGroup[]): SkillCounts {
  const counts: SkillCounts = {
    todo: 0,
    learning: 0,
    learned: 0,
    use: 0,
    unknown: 0,
    total: 0,
    taught: 0,
    ready: 0,
  };
  for (const skill of groups.flatMap((group) => group.skills)) {
    if (skill.state) counts[skill.state] += 1;
    else counts.unknown += 1;
  }
  counts.total = counts.todo + counts.learning + counts.learned + counts.use;
  counts.taught = counts.total - counts.todo;
  counts.ready = counts.learned + counts.use;
  return counts;
}

/** Closest to Ready first: fewest remaining examples, then name. */
export function closestSkills(groups: readonly LearningSkillGroup[], limit = 6) {
  return groups
    .flatMap((group) => group.skills)
    .filter((skill) => skill.state === 'learning')
    .sort(
      (a, b) =>
        (a.need ?? Number.MAX_SAFE_INTEGER) - (b.need ?? Number.MAX_SAFE_INTEGER) ||
        skillTitle(a).localeCompare(skillTitle(b)),
    )
    .slice(0, limit);
}

export const skillTitle = (skill: Pick<LearningSkill, 'kind' | 'label' | 'typeName'>) =>
  skill.kind === 'type'
    ? `Recognize ${skill.typeName}`
    : skill.kind === 'starts'
      ? skill.label
      : `${skill.typeName} · ${skill.label}`;
